/**
 * @file Popover 轻量浮层
 * @description 点击展开面板；fade；视口边界。
 *
 * @whenToUse 筛选面板、简短表单浮层。
 * @whenNotToUse 模态确认（Dialog）；纯菜单（DropdownMenu）。
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'

export type PopoverProps = {
  trigger: ReactNode
  children: ReactNode
  /** 面板宽度 class */
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Popover({
  trigger,
  children,
  className,
  open: openProp,
  onOpenChange,
}: PopoverProps) {
  const [uncontrolled, setUncontrolled] = useState(false)
  const open = openProp ?? uncontrolled
  const setOpen = (next: boolean) => {
    onOpenChange?.(next)
    if (openProp === undefined) setUncontrolled(next)
  }

  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 240))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const id = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex"
        onClick={() => setOpen(!open)}
      >
        {trigger}
      </div>
      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              className={cn(
                'fixed z-50 rounded-xl border border-border bg-raised p-3 transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
                className,
              )}
              style={floatingPanelStyle(box)}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
